import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { useShop } from './context/ShopContext';

// Every route is lazy so the initial bundle carries only the shell. Rollup then
// splits the two flows into disjoint chunk sets — LegacyRoutes and NewRoutes
// import different components — so a legacy shop never downloads the new
// Request→Quote flow, and a new shop never downloads the legacy Gadget flow.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Requests = lazy(() => import('./pages/Requests'));
const RequestDetail = lazy(() => import('./pages/RequestDetail'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const Companies = lazy(() => import('./pages/Companies'));
const Configuration = lazy(() => import('./pages/Configuration'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const Billing = lazy(() => import('./pages/Billing'));
const QuoteRequestForm = lazy(() => import('./pages/QuoteRequestForm'));
const LegacyDashboard = lazy(() => import('./pages/legacy/LegacyDashboard'));
const LegacyQuotes = lazy(() => import('./pages/legacy/LegacyQuotes'));
const LegacyQuoteDetail = lazy(() => import('./pages/legacy/LegacyQuoteDetail'));
const LegacySettings = lazy(() => import('./pages/legacy/LegacySettings'));
const LegacyButtonDesign = lazy(() => import('./pages/legacy/LegacyButtonDesign'));
const LegacyNotification = lazy(() => import('./pages/legacy/LegacyNotification'));
const LegacyFormFields = lazy(() => import('./pages/legacy/LegacyFormFields'));
const LegacyButtonConditions = lazy(() => import('./pages/legacy/LegacyButtonConditions'));
const LegacyPlanPrice = lazy(() => import('./pages/legacy/LegacyPlanPrice'));
const LegacyInstallation = lazy(() => import('./pages/legacy/LegacyInstallation'));
const LegacySupport = lazy(() => import('./pages/legacy/LegacySupport'));

/** Lightweight boundary shown while a route chunk loads. */
function RouteFallback() {
  return <div style={{ padding: '48px', textAlign: 'center', color: 'var(--s-color-text-secondary, #6b7177)' }}>Loading…</div>;
}

/**
 * Access control: every page requires an active subscription. Until the shop
 * details load we render nothing (avoids a flash of the gated UI); once loaded,
 * a shop without an active plan is redirected to Billing and confined there.
 */
function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { shop, loading } = useShop();
  const location = useLocation();

  if (loading && !shop) return null;

  const active = shop?.subscriptionActive ?? false;
  if (shop && !active && location.pathname !== '/billing') {
    return <Navigate to="/billing" replace />;
  }
  return <>{children}</>;
}

/**
 * Legacy (Gadget-migrated) shops get the reduced Quote-only experience: no
 * Request module, no approval workflow. Everything else redirects to /quotes.
 */
function LegacyRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LegacyDashboard />} />
      <Route path="/quotes" element={<LegacyQuotes />} />
      <Route path="/quotes/:id" element={<LegacyQuoteDetail />} />
      <Route path="/settings" element={<LegacySettings />} />
      <Route path="/settings/form-field" element={<LegacyFormFields />} />
      <Route path="/settings/button-condition" element={<LegacyButtonConditions />} />
      <Route path="/settings/button-design" element={<LegacyButtonDesign />} />
      <Route path="/settings/notification" element={<LegacyNotification />} />
      <Route path="/settings/pricing-plan" element={<LegacyPlanPrice />} />
      <Route path="/settings/installation" element={<LegacyInstallation />} />
      <Route path="/support" element={<LegacySupport />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** New shops: the full Request → Quote → Approval experience (unchanged). */
function NewRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/requests" element={<Requests />} />
      <Route path="/requests/new" element={<QuoteRequestForm kind="request" />} />
      <Route path="/requests/:id/edit" element={<QuoteRequestForm kind="request" />} />
      <Route path="/requests/:id" element={<RequestDetail />} />
      <Route path="/quotes" element={<Quotes />} />
      <Route path="/quotes/new" element={<QuoteRequestForm kind="quote" />} />
      <Route path="/quotes/:id/edit" element={<QuoteRequestForm kind="quote" />} />
      <Route path="/quotes/:id" element={<QuoteDetail />} />
      <Route path="/companies" element={<Companies />} />
      <Route path="/companies/:id" element={<CompanyDetail />} />
      <Route path="/configuration" element={<Configuration />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const { shop } = useShop();
  const legacy = shop?.isLegacyShop ?? false;
  const location = useLocation();

  return (
    <Layout>
      <SubscriptionGate>
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<RouteFallback />}>{legacy ? <LegacyRoutes /> : <NewRoutes />}</Suspense>
        </ErrorBoundary>
      </SubscriptionGate>
    </Layout>
  );
}
